import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Calendar,
  ChefHat,
  Loader2,
  ArrowRight,
  Heart,
  ShoppingCart,
  Clock,
  Users,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedMealPlan = () => {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadSharedPlan = async () => {
      try {
        const response = await fetch(`${API_URL}/api/shared/${shareId}`);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to load meal plan');
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadSharedPlan();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading meal plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Meal Plan Not Found</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>
                Go to Harvest & Hearth
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const days = data?.meal_plan?.days || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/5 to-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl">🌿</span>
            <span className="font-['Playfair_Display'] text-xl font-semibold text-primary">
              Harvest & Hearth
            </span>
          </Link>
          <Link to="/">
            <Button variant="outline" size="sm">
              Create Your Own
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <Badge variant="secondary" className="mb-4">
            <Calendar className="h-3 w-3 mr-1" />
            Shared Meal Plan
          </Badge>
          <h1 className="text-3xl md:text-4xl font-['Playfair_Display'] font-semibold mb-2">
            {data?.shared_by}&apos;s Meal Plan
          </h1>
          <p className="text-muted-foreground">
            Shared on {new Date(data?.shared_at).toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </p>
        </div>

        {/* Meal Plan Grid */}
        {days.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {days.map((day, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2 bg-gradient-to-r from-primary/5 to-transparent">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    {new Date(day.date).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  {day.meals && day.meals.length > 0 ? (
                    <div className="space-y-3">
                      {day.meals.map((meal, mealIndex) => (
                        <div 
                          key={mealIndex}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          {meal.recipe_image ? (
                            <img
                              src={meal.recipe_image}
                              alt={meal.recipe_title}
                              className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <ChefHat className="h-6 w-6 text-primary/50" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {meal.recipe_title || 'Untitled Recipe'}
                            </p>
                            <p className="text-xs text-muted-foreground capitalize">
                              {meal.meal_type || 'Meal'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No meals planned
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <ChefHat className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">This meal plan is empty</p>
            </CardContent>
          </Card>
        )}

        {/* CTA Section */}
        <Card className="mt-8 border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
          <CardContent className="py-8 text-center">
            <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-2">
              Want to create your own meal plan?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Plan your meals, generate shopping lists, and discover new recipes with Harvest & Hearth.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/">
                <Button size="lg" className="gap-2">
                  <ChefHat className="h-5 w-5" />
                  Get Started Free
                </Button>
              </Link>
              <Link to="/recipes">
                <Button size="lg" variant="outline" className="gap-2">
                  <Heart className="h-5 w-5" />
                  Browse Recipes
                </Button>
              </Link>
            </div>
            
            {/* Features */}
            <div className="flex flex-wrap gap-6 justify-center mt-8 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                Weekly Meal Planning
              </span>
              <span className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-primary" />
                Auto Shopping Lists
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                Quick & Easy Recipes
              </span>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 mt-12 py-8 text-center text-sm text-muted-foreground">
        <p>
          Made with 🌿 by{' '}
          <Link to="/" className="text-primary hover:underline">
            Harvest & Hearth
          </Link>
        </p>
      </footer>
    </div>
  );
};

export default SharedMealPlan;
